import { DeterminateProgressBar } from 'cdk-radial';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { REDUX_SLICE_KEYS, EVENT_NAME } from 'common/constants';
import { Dialog, ErrorToastValidation } from 'components';

import { useAmplitude } from 'components/AmplitudeProvider';
import { DropdownOption } from 'components/Dropdown/types';
import { setErrorMessages } from 'components/ErrorMessageHandler/slice';
import { getIsValidPaymentDate } from 'components/PaymentDatePicker/slice';
import { useFlagValue } from 'hooks';
import {
  useFetchCashInBankInterCompany,
  useFetchPrintersWithCheckNo,
  useInterCompanyPrintChecks
} from 'hooks/useClientDataServices';
import { AuthorizePaymentHeader } from 'pages/AuthorizePaymentPage/components/AuthorizePaymentHeader/AuthorizePaymentHeader';
import { updateActiveAuthorizePaymentPage } from 'pages/VendorPaymentResultPage/slice';
import {
  PrintFailureDialog,
  PrintSuccessDialog
} from 'pages/VendorsTablePage/components/dialogs';
import { CheckSequence } from 'pages/VendorsTablePage/components/PrintChecks/CheckSequence/CheckSequence';
import { errorMessageChkSeq } from 'pages/VendorsTablePage/components/PrintChecks/CheckSequence/messages';
import { getAllPrinterErrors } from 'pages/VendorsTablePage/components/PrintChecks/CheckSequence/slice';
import { ReviewChecksTable } from 'pages/VendorsTablePage/components/PrintChecks/ReviewChecksTable';
import {
  getPrintChecksPayload,
  getManualCheckDetails,
  setIsPrintSuccessDialogOpen,
  getIsPrintSuccessDialogOpen,
  setIsPrintFailureDialogOpen,
  clearCheckWritingResponse,
  getIsPrintFailureDialogOpen,
  setCheckWritingResponse,
  setInterCompanyCIBAccount,
  setCheckNoInputError,
  getSelectedVendors,
  getArchiveOnly,
  getInterCompanyCIBAccount
} from 'pages/VendorsTablePage/components/PrintChecks/slice';
import { getNumberFromCurrency } from 'pages/VendorsTablePage/components/VendorsTable/helpers';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { amplitudeTracker } from 'utils/AmplitudeTracker';
import { handleJsonError } from 'utils/handleJsonError';
import { getStoreIdByCompanyId } from './interCompany/helpers';
import { InterCompanyFields } from './interCompany/InterCompanyFields';
import { CashInBankAccounts, IntercompanyJournals } from './interCompany/types';
import { labels } from './messages';
import { StyledTextContainer } from './styled';

export const ReviewChecksPage = (): React.JSX.Element => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const amplitude = useAmplitude();
  const isPrintSuccessDialogOpen = useAppSelector(getIsPrintSuccessDialogOpen);
  const isPrintFailureDialogOpen = useAppSelector(getIsPrintFailureDialogOpen);
  const manualCheckDetailsPayload = useAppSelector(getManualCheckDetails);
  const printChecksPayload = useAppSelector(getPrintChecksPayload);
  const selectedVendors = useAppSelector(getSelectedVendors);
  const [selectedJournal, setSelectedJournal] = useState<DropdownOption>();
  const [postingDescription, setPostingDesc] = useState<string>('');
  const [currentCheck, setCurrentCheck] = useState(0);
  const [totalChecks, setTotalChecks] = useState(
    printChecksPayload.vendors.length
  );
  const [checkNoInputValue, setCheckNoInputValue] = useState<string>('');
  const [isRadioOptionEnabled, setIsRadioOptionEnabled] =
    useState<boolean>(false);
  const [selectedCashCompany, setSelectedCashCompany] =
    useState<DropdownOption>();
  const [selectedCashInBank, setSelectedCashInBank] =
    useState<DropdownOption>();
  const [errorToast, setErrorToast] = useState(false);
  const [noChecksErrorToast, setNoChecksErrorToast] = useState(false);
  const printerErrors = useAppSelector(getAllPrinterErrors);
  const { checkTrayError, overflowTrayError, printerError } = printerErrors;
  const archiveOnly = useAppSelector(getArchiveOnly);

  const percentComplete = 100 * (currentCheck / totalChecks);
  const interCompanyCIBAccount = useAppSelector(getInterCompanyCIBAccount);
  const { data: cibAccountData } = useFetchCashInBankInterCompany(false);
  const cashCompanyId = selectedCashCompany?.value;

  const {
    isLoading: isPrintChecksLoading,
    data,
    mutate: printChecks
  } = useInterCompanyPrintChecks(({ current, total }) => {
    setCurrentCheck(current ?? 0);
    setTotalChecks(total ?? printChecksPayload.vendors.length);
  });
  const checkSequencingFlag = useFlagValue('dmsAccountingCheckSequencing');

  const cashInBankAccountNumber = selectedCashInBank?.label.split(' ')[0];
  const selectedCashInBankValue = selectedCashInBank?.value;

  const cashCompanyStoreid = useMemo(
    () => getStoreIdByCompanyId(cibAccountData?.cashCompanies, cashCompanyId),
    [cibAccountData?.cashCompanies, cashCompanyId]
  );
  const { isError } = useFetchPrintersWithCheckNo(
    interCompanyCIBAccount.accountNumber,
    cashCompanyStoreid
  );

  const journalSelectedObj = useMemo(
    () =>
      selectedJournal?.value ? handleJsonError(selectedJournal?.value) : null,
    [selectedJournal?.value]
  ) as IntercompanyJournals;

  const isValidPaymentDate = useAppSelector(getIsValidPaymentDate);

  const isPostingDescriptionRequired = useMemo(() => {
    if (!selectedCashInBankValue) return true;
    const filteredData = cibAccountData?.cashAccounts?.filter(
      accountData => accountData?.accountNumber === selectedCashInBankValue
    )[0]?.postingDescriptionRequired;
    return filteredData;
  }, [cibAccountData?.cashAccounts, selectedCashInBankValue]);

  const handleAuthorizePayment = () => {
    if (selectedVendors.length === 0) {
      setNoChecksErrorToast(true);
    } else if (checkTrayError || overflowTrayError || printerError) {
      setErrorToast(true);
    } else if (isError && archiveOnly) {
      dispatch(
        setErrorMessages([intl.formatMessage(errorMessageChkSeq.noEformsSetup)])
      );
    } else if (isRadioOptionEnabled && checkNoInputValue === '') {
      dispatch(setCheckNoInputError(true));
    } else if (
      isValidPaymentDate &&
      (!isPostingDescriptionRequired ||
        (isPostingDescriptionRequired && postingDescription !== ''))
    ) {
      const manualCheckDetails = {
        dmsPrinterNo: manualCheckDetailsPayload?.dmsPrinterNo || null,
        printerId: manualCheckDetailsPayload?.printerId || null,
        archiveOnly: manualCheckDetailsPayload?.archiveOnly,
        manualEntryCheckSequence:
          checkNoInputValue &&
          manualCheckDetailsPayload?.isSequenceForFutureChecks !== null
            ? {
                checkNumber: checkNoInputValue,
                useSequenceForFutureChecks:
                  manualCheckDetailsPayload?.isSequenceForFutureChecks
              }
            : null
      };
      const payload = {
        cashInBankAccount: cashInBankAccountNumber,
        paymentDate: printChecksPayload.paymentDate,
        printOrder: printChecksPayload.printOrder,
        postingDescription,
        vendors: printChecksPayload.vendors,
        cashCompanyId,
        cashInBankStoreId: cashCompanyStoreid,
        journalNumber: journalSelectedObj?.journalNumber,
        journalCompanyId: journalSelectedObj?.companyId,
        journalStoreId: journalSelectedObj?.storeId,
        journalName: journalSelectedObj?.journalName,
        ...(checkSequencingFlag && manualCheckDetails)
      };
      printChecks(payload);
      amplitudeTracker(amplitude, EVENT_NAME.PrintChecks, {
        'Vendor Total': printChecksPayload.vendors.length || 0,
        'Invoice Total': printChecksPayload.vendors.reduce(
          (acc, checks) => acc + getNumberFromCurrency(checks.totalNetAmount),
          0
        )
      });
    }
  };

  const handleCashInBankAccounts = useCallback(
    (selectedValue: DropdownOption) => {
      setSelectedCashInBank(selectedValue);
      const selectedCIBAccount = cibAccountData?.cashAccounts.find(
        (cashInBankAcc: CashInBankAccounts) =>
          cashInBankAcc.accountNumber === selectedValue.value
      );
      if (selectedCIBAccount) {
        dispatch(setInterCompanyCIBAccount(selectedCIBAccount));
      }
    },
    [cibAccountData?.cashAccounts, dispatch]
  );

  const onFailureDialogClose = () => {
    dispatch(setIsPrintFailureDialogOpen(false));
    dispatch(clearCheckWritingResponse());
    dispatch(updateActiveAuthorizePaymentPage(''));
    dispatch(
      setInterCompanyCIBAccount({
        companyId: '',
        accountName: '',
        accountNumber: ''
      })
    );
  };

  useEffect(() => {
    if (data) {
      const { printOkList, printFailureList } = data;
      dispatch(setCheckWritingResponse(data));
      if (printOkList.length > 0) {
        dispatch(setIsPrintSuccessDialogOpen(true));
      }
      if (printOkList.length === 0 && printFailureList.length > 0)
        dispatch(setIsPrintFailureDialogOpen(true));
    }
  }, [data, dispatch]);

  return (
    <>
      <AuthorizePaymentHeader
        handleAuthorizePayment={handleAuthorizePayment}
        isLoading={false}
      />
      {noChecksErrorToast && (
        <ErrorToastValidation
          errorMessage={intl.formatMessage(errorMessageChkSeq.noChecksSelected)}
          handleDismiss={() => setNoChecksErrorToast(false)}
          onlyOneButtonProps
        />
      )}
      {errorToast && (
        <ErrorToastValidation
          errorMessage={intl.formatMessage(
            errorMessageChkSeq.noPrinterSelection
          )}
          handleDismiss={() => setErrorToast(false)}
          onlyOneButtonProps
        />
      )}
      <InterCompanyFields
        whichScreen={REDUX_SLICE_KEYS.printChecks}
        setSelectedCashCompany={setSelectedCashCompany}
        setSelectedCashInBank={handleCashInBankAccounts}
        setSelectedJournal={setSelectedJournal}
        setPostingDesc={setPostingDesc}
        selectedCashCompany={selectedCashCompany}
        postingDescription={postingDescription}
        isPostingDescriptionRequired={isPostingDescriptionRequired}
      />
      {checkSequencingFlag && (
        <CheckSequence
          setCheckNoInputValue={setCheckNoInputValue}
          checkNoInputValue={checkNoInputValue}
          setIsRadioOptionEnabled={setIsRadioOptionEnabled}
          cashCompanyStoreid={cashCompanyStoreid}
        />
      )}
      <Dialog
        title={intl.formatMessage(
          manualCheckDetailsPayload?.archiveOnly
            ? labels.archiveChecksLabel
            : labels.printingChecksLabel
        )}
        open={isPrintChecksLoading}
        dataTestId="progress-dialog"
        id="progress-dialog"
        hideCloseButton
        hideHeader
      >
        <StyledTextContainer>
          <div>
            {intl.formatMessage(
              manualCheckDetailsPayload?.archiveOnly
                ? labels.archiveChecksLabel
                : labels.printingChecksLabel
            )}
          </div>
          <div>
            {currentCheck} of {totalChecks} complete
          </div>
        </StyledTextContainer>
        <DeterminateProgressBar
          dataTestId="indeterminate-progress-bar"
          hideLabel
          hideProgress
          percentComplete={percentComplete}
          trackWidth="100%"
        />
      </Dialog>
      <PrintSuccessDialog
        open={isPrintSuccessDialogOpen}
        journalStoreId={journalSelectedObj?.storeId}
        journalNumber={journalSelectedObj?.journalNumber}
        cashInBankAccount={cashInBankAccountNumber}
        postingDesc={postingDescription}
        journalName={journalSelectedObj?.journalName}
        journalCompanyId={journalSelectedObj?.companyId}
        cashInBankStoreId={cashCompanyStoreid}
        cashCompanyId={cashCompanyId}
      />
      <PrintFailureDialog
        open={isPrintFailureDialogOpen}
        close={onFailureDialogClose}
      />
      <ReviewChecksTable
        selectedCashCompany={selectedCashCompany}
        selectedCashInBank={selectedCashInBank}
        selectedJournal={selectedJournal}
        postingDescription={postingDescription}
      />
    </>
  );
};
